/**
 * features/dashboard/Dashboard.tsx
 * Refined minimalist dashboard for the LKC Academic Platform.
 * Clean, institutional, action-oriented — no gradient cards.
 */
import {
  FileMinus,
  CalendarClock,
  Users2,
  HeartHandshake,
  Users,
  GraduationCap,
  BookOpen,
  ClipboardList,
  ArrowRight,
  PenSquare,
  CalendarCheck,
  MessageSquare,
  ClipboardCheck,
  Flag,
  FileText,
  Send,
  BarChart3,
  ShieldCheck,
  UserCog,
  UserPlus,
  ShieldAlert,
  Award,
  ArrowUpDown,
  Banknote,
  HandCoins,
  BellRing,
  LayoutDashboard,
  SlidersHorizontal,
  Play,
  FilePlus2,
  FileSpreadsheet,
  ArrowLeftRight,
  ListTree,
  BadgePercent,
  Clock,
  Calendar,
  Star,
  UserMinus,
  HeartPulse,
  Landmark,
  Calculator,
  HelpCircle,
  CalendarRange,
  UserRoundCheck,
  type LucideIcon,
} from 'lucide-react';
import { useData } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import HeadDashboard from '@/features/dashboard/HeadDashboard';
import { SCHOOL } from '@/lib/school.config';
import { ROLES, getPermissions } from '@/lib/permissions';
import { Card } from '@/components/ui/primitives';
import DashboardStudentSearch from './DashboardStudentSearch';
import AnnouncementsBanner from './AnnouncementsBanner';
import { NAV, SECTION_ORDER, placeTab, sectionLabel, type TabId } from '@/components/Shell';

/* ==================================================================
   ACTION CARD DEFINITIONS
   ================================================================== */

/** The eight sections the money moves through. Used by the dev-only coverage
 *  check below — these are the ones a missing dashboard card actually hides,
 *  because setup and admin screens are reached from the setup hub as well. */
const MONEY_SECTIONS = new Set<string>([
  'feeBilling', 'feeCollect', 'purchasing', 'payables',
  'cashbank', 'ledger', 'finReports', 'finSetup',
]);

interface ActionCard {
  tab: TabId;
  title: string;
  desc: string;
  icon: LucideIcon;
  accent: string; // tailwind border/text color
  /* No `section` field. A card belongs to whichever section its TAB belongs to
     in the sidebar — see sectionOf() below. It used to be stated twice, here
     and in Shell, and the two drifted: the sidebar was regrouped into eight
     finance sections while this file still spoke of one flat "Finance". */
  show: boolean;
}

export default function Dashboard({
  onNavigate,
  year,
  term,
}: {
  onNavigate: (t: TabId, opts?: { studentId?: string }) => void;
  year?: string;
  term?: string;
}) {
  const { teachers, activeStudents: students, subjects, classes, loading } = useData();
  const { userData, userRole, userRoles } = useAuth();
  const roleMeta = userRole ? ROLES[userRole] : null;
  const p = getPermissions(userRoles);

  const cards: ActionCard[] = [
    // Academic workflow
    {
      tab: 'marks',
      title: 'Mark Entry',
      desc: 'CA and exam marks',
      icon: PenSquare,
      accent: 'border-l-primary text-primary',
      show: p.canEnterMarks,
    },
    {
      tab: 'attendance',
      title: 'Attendance',
      desc: 'Daily student attendance',
      icon: CalendarCheck,
      accent: 'border-l-emerald-500 text-emerald-600',
      show: p.canEnterClassTeacherComment,
    },
    {
      tab: 'classComment',
      title: 'Class Comment',
      desc: 'Conduct per student',
      icon: MessageSquare,
      accent: 'border-l-violet-500 text-violet-600',
      show: p.canEnterClassTeacherComment,
    },
    {
      tab: 'hodComment',
      title: 'HOY Comment',
      desc: 'Head-of-year remark',
      icon: MessageSquare,
      accent: 'border-l-pink-500 text-pink-600',
      show: p.canEnterHodComment,
    },
    {
      tab: 'commentReview',
      title: 'Comment Review',
      desc: 'Review all comments',
      icon: ClipboardCheck,
      accent: 'border-l-primary text-primary',
      show: p.canEditCommentsOnReport,
    },
    {
      tab: 'flags',
      title: 'Comment Flags',
      desc: 'Flagged items',
      icon: Flag,
      accent: 'border-l-amber-500 text-amber-600',
      show: p.canRaiseFlags || p.canManageFlags,
    },
    {
      tab: 'discipline',
      title: 'Discipline',
      desc: 'Record incidents',
      icon: ShieldAlert,
      accent: 'border-l-rose-500 text-rose-600',
      show: p.canManageDiscipline,
    },
    {
      tab: 'achievements',
      title: 'Achievements',
      desc: 'Celebrate students',
      icon: Award,
      accent: 'border-l-amber-500 text-amber-600',
      show: p.canManageAchievements,
    },
    // Reports
    {
      tab: 'reports',
      title: 'Generate Reports',
      desc: 'Produce report cards',
      icon: FileText,
      accent: 'border-l-primary text-primary',
      show: p.canGenerateReports,
    },
    {
      tab: 'viewReports',
      title: 'View Reports',
      desc: 'Preview report cards',
      icon: FileText,
      accent: 'border-l-emerald-500 text-emerald-600',
      show: p.canViewOwnClassReports,
    },
    {
      tab: 'bulkSend',
      title: 'Send Reports',
      desc: 'Email to parents',
      icon: Send,
      accent: 'border-l-violet-500 text-violet-600',
      show: p.canBulkSendReports,
    },
    {
      tab: 'marksheet',
      title: 'Student Analysis',
      desc: 'Marksheets, at-risk & summaries',
      icon: BarChart3,
      accent: 'border-l-primary text-primary',
      show: p.canViewProgress,
    },
    {
      tab: 'teacherAnalysis',
      title: 'Teacher & Dept',
      desc: 'Teacher & department analytics',
      icon: BarChart3,
      accent: 'border-l-emerald-500 text-emerald-600',
      show: p.canViewProgress,
    },
    // Exams
    {
      tab: 'exams',
      title: 'Exams',
      desc: 'Class lists, registers & seating plans',
      icon: ClipboardList,
      accent: 'border-l-indigo-500 text-indigo-600',
      show: p.canManageExams,
    },
    // Finance
    { tab: 'financeOverview', title: 'Finance Overview', desc: 'Balances & totals', icon: LayoutDashboard, accent: 'border-l-primary text-primary', show: p.canViewFinanceTotals },
    { tab: 'financePayments', title: 'Payments', desc: 'Record receipts', icon: HandCoins, accent: 'border-l-emerald-500 text-emerald-600', show: p.canAccessFinance },
    { tab: 'financeUnidentified', title: 'Unidentified Payments', desc: 'Allocate unreferenced bank credits', icon: HelpCircle, accent: 'border-l-amber-500 text-amber-600', show: p.canAccessFinance },
    { tab: 'financeCashUp', title: 'Cash Up', desc: 'Daily takings reconciliation', icon: Calculator, accent: 'border-l-amber-500 text-amber-600', show: p.canAccessFinance },
    { tab: 'financeDebtors', title: 'Debtors', desc: 'Who owes what, by family', icon: Users2, accent: 'border-l-rose-500 text-rose-600', show: p.canViewFinanceTotals },
    { tab: 'financeArrangements', title: 'Payment Plans', desc: 'Instalment plans for arrears', icon: CalendarClock, accent: 'border-l-violet-500 text-violet-600', show: p.canAccessFinance },
    { tab: 'financeSiblingDiscounts', title: 'Sibling Discounts', desc: 'Sibling concessions, per pupil', icon: BadgePercent, accent: 'border-l-teal-500 text-teal-600', show: p.canAccessFinance },
    { tab: 'financeAdjustments', title: 'Credit Notes', desc: 'Credit notes and write-offs', icon: FileMinus, accent: 'border-l-amber-500 text-amber-600', show: p.canAccessFinance },
    { tab: 'financeArrears', title: 'Arrears', desc: 'Overdue & reminders', icon: BellRing, accent: 'border-l-rose-500 text-rose-600', show: p.canViewFinanceTotals },
    { tab: 'financeBilling', title: 'Billing', desc: 'Run term billing', icon: Play, accent: 'border-l-violet-500 text-violet-600', show: p.canViewFinanceTotals },
    { tab: 'financeInvoice', title: 'New Invoice', desc: 'Raise an invoice', icon: FilePlus2, accent: 'border-l-amber-500 text-amber-600', show: p.canAccessFinance },
    { tab: 'financeExamFees', title: 'Bulk Invoices', desc: 'Exam & batch fees', icon: FileSpreadsheet, accent: 'border-l-pink-500 text-pink-600', show: p.canAccessFinance },
    { tab: 'financeStatements', title: 'Statements', desc: 'Family statements', icon: FileText, accent: 'border-l-emerald-500 text-emerald-600', show: p.canViewFinanceTotals },
    { tab: 'financeSpecialCases', title: 'Fee Special Cases', desc: 'Prepayment, bursaries, staff children', icon: HeartHandshake, accent: 'border-l-teal-500 text-teal-600', show: p.canAccessFinance },
    { tab: 'financeFees', title: 'Fee Setup', desc: 'Fee configuration', icon: SlidersHorizontal, accent: 'border-l-primary text-primary', show: p.canManageFinanceConfig },
    { tab: 'financeReconcile', title: 'Reconcile', desc: 'Bank reconciliation', icon: ArrowLeftRight, accent: 'border-l-violet-500 text-violet-600', show: p.canAccessFinance },
    { tab: 'financeJournals', title: 'Journals', desc: 'Post and review ledger entries', icon: BookOpen, accent: 'border-l-primary text-primary', show: p.canViewGeneralLedger },
    { tab: 'financeGeneralLedger', title: 'General Ledger', desc: 'Trial balance and account movements', icon: Landmark, accent: 'border-l-primary text-primary', show: p.canViewGeneralLedger },
    { tab: 'financeChartOfAccounts', title: 'Chart of Accounts', desc: 'Accounts and where each thing posts', icon: ListTree, accent: 'border-l-primary text-primary', show: p.canManageChartOfAccounts },
    // HR & Payroll
    { tab: 'hrStaff', title: 'Staff Directory', desc: 'People & records', icon: Users, accent: 'border-l-primary text-primary', show: p.canAccessHR },
    { tab: 'hrLeave', title: 'Leave', desc: 'Applications & balances', icon: Calendar, accent: 'border-l-emerald-500 text-emerald-600', show: p.canAccessHR },
    { tab: 'hrContracts', title: 'Contracts', desc: 'Staff contracts', icon: FileText, accent: 'border-l-violet-500 text-violet-600', show: p.canAccessHR },
    { tab: 'hrAppraisals', title: 'Appraisals', desc: 'Performance reviews', icon: Star, accent: 'border-l-amber-500 text-amber-600', show: p.canAccessHR },
    { tab: 'hrCompliance', title: 'Compliance', desc: 'Documents & checks', icon: ShieldCheck, accent: 'border-l-rose-500 text-rose-600', show: p.canAccessHR },
    { tab: 'hrClocking', title: 'Clocking', desc: 'Attendance & hours', icon: Clock, accent: 'border-l-pink-500 text-pink-600', show: p.canViewClocking },
    { tab: 'hrPayroll', title: 'Payroll', desc: 'Run payroll', icon: Banknote, accent: 'border-l-primary text-primary', show: p.canAccessPayroll },
    { tab: 'hrSeverance', title: 'Severance', desc: 'Exit settlements', icon: UserMinus, accent: 'border-l-emerald-500 text-emerald-600', show: p.canAccessPayroll },
    { tab: 'hrLoans', title: 'Loans & Advances', desc: 'Staff loans', icon: HandCoins, accent: 'border-l-violet-500 text-violet-600', show: p.canAccessPayroll },
    { tab: 'hrMedicalAid', title: 'Medical Aid', desc: 'Scheme management', icon: HeartPulse, accent: 'border-l-rose-500 text-rose-600', show: p.canAccessPayroll },
    { tab: 'hrPaymentDetails', title: 'Payment Details', desc: 'Bank & pay info', icon: Landmark, accent: 'border-l-amber-500 text-amber-600', show: p.canAccessPayroll },
    { tab: 'hrBurs', title: 'BURS Returns', desc: 'Statutory returns', icon: Landmark, accent: 'border-l-pink-500 text-pink-600', show: p.canAccessPayroll },
    { tab: 'hrTaxTables', title: 'Tax Tables', desc: 'PAYE configuration', icon: Calculator, accent: 'border-l-primary text-primary', show: p.canManageTaxTables },
    // Academic Admin
    {
      tab: 'admissions',
      title: 'Admissions',
      desc: 'Student intake',
      icon: UserPlus,
      accent: 'border-l-amber-500 text-amber-600',
      show: p.canAccessAdmissions,
    },
    {
      tab: 'promotion',
      title: 'Promotion',
      desc: 'Year-end promotions',
      icon: ArrowUpDown,
      accent: 'border-l-violet-500 text-violet-600',
      show: p.canManageTerms,
    },
    // System
    {
      tab: 'setupHub',
      title: 'Setup',
      desc: 'Core configuration',
      icon: ClipboardList,
      accent: 'border-l-amber-500 text-amber-600',
      show: p.canAccessSetup,
    },
    {
      tab: 'users',
      title: 'User Management',
      desc: 'Accounts & roles',
      icon: ShieldCheck,
      accent: 'border-l-rose-500 text-rose-600',
      show: p.canManageUsers,
    },
    {
      tab: 'account',
      title: 'My Account',
      desc: 'Profile & password',
      icon: UserCog,
      accent: 'border-l-gray-400 text-gray-500',
      show: true,
    },
    // School Settings
    {
      tab: 'parentOnboarding',
      title: 'Parent Onboarding',
      desc: 'Invite & link parents',
      icon: UserRoundCheck,
      accent: 'border-l-emerald-500 text-emerald-600',
      show: p.canManageParentSettings,
    },
    {
      tab: 'publicHolidays',
      title: 'Public Holidays',
      desc: 'Calendar of public holidays',
      icon: CalendarRange,
      accent: 'border-l-primary text-primary',
      show: p.canAccessHR,
    },
  ];

  /* Sections and their order come from the SIDEBAR, not from a second list kept
     here. They were maintained separately and had already drifted — the sidebar
     regrouped its finance screens into eight while this list still offered one
     "Finance" heading, so a card could sit under a heading no sidebar section
     matched. A card now belongs to whichever section its tab belongs to, and
     the two cannot disagree again.

     The fallback only fires for a card whose tab has no nav entry at all; every
     card today has one. 'system' is the honest place for such a card — visible
     and obviously misfiled — rather than dropped. */
  const sectionOf = (tab: TabId): (typeof SECTION_ORDER)[number] =>
    placeTab(tab, p, userData?.linkedTeacherId);

  /* A heading the dashboard words better than the sidebar does.
     'main' has no sidebar heading at all (it sits above the first divider), and
     a section with an empty label would render its cards under a blank line —
     which is how the "My Account" card would otherwise have disappeared. */
  const DASHBOARD_SECTION_LABELS: Partial<Record<(typeof SECTION_ORDER)[number], string>> = {
    main: 'General',
  };

  const sections = SECTION_ORDER.map((key) => ({
    key,
    label: DASHBOARD_SECTION_LABELS[key] ?? sectionLabel(key, userRoles),
  }));

  /* COVERAGE, IN DEVELOPMENT ONLY.
     The accounting layer adds screens in batches, and a finance screen that is
     built, routed and listed in the sidebar but has no dashboard card is
     invisible to anyone who works from the dashboard — which is most people,
     most days. That failure is silent: the screen works perfectly for whoever
     knows the sidebar entry exists.

     So the dashboard says so out loud while it is being built. Dev-only: this
     is a note to whoever is adding the screen, not a warning for a bursar. */
  if (import.meta.env.DEV) {
    const covered = new Set(cards.map((c) => c.tab));
    const uncovered = NAV.filter(
      (n) => MONEY_SECTIONS.has(n.section) && !covered.has(n.id),
    );
    if (uncovered.length > 0) {
      console.warn(
        `[dashboard] ${uncovered.length} finance screen(s) in the sidebar have no dashboard card:`,
        uncovered.map((n) => `${n.id} (${n.section})`).join(', '),
      );
    }
  }

  // Admin setup checklist
  const setupSteps = [
    { done: subjects.length > 0, label: 'Configure subjects', tab: 'subjects' },
    { done: teachers.length > 0, label: 'Add teachers', tab: 'teachers' },
    { done: classes.length > 0, label: 'Create classes', tab: 'classes' },
    { done: students.length > 0, label: 'Enrol students', tab: 'students' },
  ] as const;
  const completed = setupSteps.filter((s) => s.done).length;

  const quick = [
    { label: 'Students', value: students.length, icon: GraduationCap },
    { label: 'Teachers', value: teachers.length, icon: Users },
    { label: 'Subjects', value: subjects.length, icon: BookOpen },
    { label: 'Classes', value: classes.length, icon: ClipboardList },
  ];

  // Head of School landing: a snapshot instead of the action-card grid.
  if (userRole === 'headteacher') {
    return (
      <HeadDashboard
        onNavigate={onNavigate}
        year={year ?? String(new Date().getFullYear())}
        term={term ?? ''}
      />
    );
  }

  return (
    <div className="space-y-6 relative">
      {/* Round 8: subtle gradient layer for the glass aesthetic. Scoped
          to the dashboard - other screens are untouched. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,rgba(27,58,107,0.06),transparent_40%),radial-gradient(circle_at_bottom_right,rgba(212,169,44,0.05),transparent_45%)] dark:bg-[radial-gradient(circle_at_top_left,rgba(45,82,149,0.18),transparent_40%),radial-gradient(circle_at_bottom_right,rgba(212,169,44,0.08),transparent_45%)]"
      />
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-r from-primary to-primaryDark text-white px-6 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">
              Welcome back, {userData?.displayName}
            </h1>
            {roleMeta && (
              <p className="text-white/50 text-sm mt-0.5">
                {roleMeta.label} · {SCHOOL.platformName}
              </p>
            )}
          </div>
          {p.canAccessSetup && (
            <div className="hidden sm:flex items-center gap-5">
              {quick.map((q) => (
                <div key={q.label} className="text-center">
                  <p className="text-lg font-bold leading-none">
                    {loading ? '—' : q.value}
                  </p>
                  <p className="text-[10px] text-white/40 mt-0.5">
                    {q.label}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Announcements */}
      <AnnouncementsBanner />

      {/* Search */}
      <DashboardStudentSearch onNavigate={onNavigate} />

      {/* Mobile stats (only show on small screens for admin) */}
      {p.canAccessSetup && (
        <div className="grid grid-cols-4 gap-3 sm:hidden">
          {quick.map((q) => {
            return (
              <div
                key={q.label}
                className="rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/70 shadow-md px-3 py-2.5 text-center"
              >
                <p className="text-lg font-bold text-gray-800 dark:text-white">
                  {loading ? '—' : q.value}
                </p>
                <p className="text-[10px] text-gray-400">{q.label}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Action sections */}
      {sections.map((section, sIdx) => {
        const sectionCards = cards.filter(
          (c) => c.show && sectionOf(c.tab) === section.key,
        );
        if (sectionCards.length === 0) return null;
        return (
          <div key={section.key} className={sIdx > 0 ? 'pt-2 border-t border-gray-200 dark:border-gray-700/50' : ''}>
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              {section.label}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {sectionCards.map((c) => {
                const Icon = c.icon;
                const accentBorder = c.accent.split(' ')[0];
                const accentText = c.accent.split(' ')[1] ?? 'text-gray-400';
                // Round 9: navigation cards float by default (mobile
                // had no hover treatment, so they looked flat). On
                // hover/touch the icon tile deepens its gradient,
                // the shadow grows, and the card lifts slightly more
                // for tactile feedback. Solid surface instead of
                // backdrop-blur — feels more premium on Android.
                //
                // The accent ramps follow the existing per-card
                // palette (emerald / violet / pink / amber / rose /
                // navy-primary / gray default).
                const iconTileBase =
                  accentText.includes('primary')
                    ? 'bg-gradient-to-br from-primary/10 to-primaryLight/20 group-hover:from-primary/20 group-hover:to-primaryLight/35 group-active:from-primary/20 group-active:to-primaryLight/35'
                    : accentText.includes('emerald')
                    ? 'bg-gradient-to-br from-emerald-50 to-emerald-100 group-hover:from-emerald-100 group-hover:to-emerald-200 group-active:from-emerald-100 group-active:to-emerald-200 dark:from-emerald-900/30 dark:to-emerald-800/20 dark:group-hover:from-emerald-800/50 dark:group-hover:to-emerald-700/40 dark:group-active:from-emerald-800/50 dark:group-active:to-emerald-700/40'
                    : accentText.includes('violet')
                    ? 'bg-gradient-to-br from-violet-50 to-violet-100 group-hover:from-violet-100 group-hover:to-violet-200 group-active:from-violet-100 group-active:to-violet-200 dark:from-violet-900/30 dark:to-violet-800/20 dark:group-hover:from-violet-800/50 dark:group-hover:to-violet-700/40 dark:group-active:from-violet-800/50 dark:group-active:to-violet-700/40'
                    : accentText.includes('pink')
                    ? 'bg-gradient-to-br from-pink-50 to-pink-100 group-hover:from-pink-100 group-hover:to-pink-200 group-active:from-pink-100 group-active:to-pink-200 dark:from-pink-900/30 dark:to-pink-800/20 dark:group-hover:from-pink-800/50 dark:group-hover:to-pink-700/40 dark:group-active:from-pink-800/50 dark:group-active:to-pink-700/40'
                    : accentText.includes('amber')
                    ? 'bg-gradient-to-br from-amber-50 to-amber-100 group-hover:from-amber-100 group-hover:to-amber-200 group-active:from-amber-100 group-active:to-amber-200 dark:from-amber-900/30 dark:to-amber-800/20 dark:group-hover:from-amber-800/50 dark:group-hover:to-amber-700/40 dark:group-active:from-amber-800/50 dark:group-active:to-amber-700/40'
                    : accentText.includes('rose')
                    ? 'bg-gradient-to-br from-rose-50 to-rose-100 group-hover:from-rose-100 group-hover:to-rose-200 group-active:from-rose-100 group-active:to-rose-200 dark:from-rose-900/30 dark:to-rose-800/20 dark:group-hover:from-rose-800/50 dark:group-hover:to-rose-700/40 dark:group-active:from-rose-800/50 dark:group-active:to-rose-700/40'
                    : 'bg-gradient-to-br from-gray-50 to-gray-100 group-hover:from-gray-100 group-hover:to-gray-200 group-active:from-gray-100 group-active:to-gray-200 dark:from-gray-700/50 dark:to-gray-600/30 dark:group-hover:from-gray-600/60 dark:group-hover:to-gray-500/40 dark:group-active:from-gray-600/60 dark:group-active:to-gray-500/40';
                return (
                  <button
                    key={c.tab}
                    onClick={() => onNavigate(c.tab)}
                    className={`text-left rounded-r-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/70 border-l-4 ${accentBorder} px-4 py-4 shadow-md -translate-y-px hover:shadow-xl hover:-translate-y-1 active:shadow-lg active:-translate-y-0.5 transition-all duration-200 group`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <div
                          className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-sm group-hover:shadow-md group-active:shadow-md transition-all duration-200 ${iconTileBase}`}
                        >
                          <Icon size={19} className={accentText} />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-semibold text-sm text-gray-800 dark:text-white leading-tight">
                            {c.title}
                          </h3>
                          <p className="text-xs text-gray-400 mt-0.5 truncate">
                            {c.desc}
                          </p>
                        </div>
                      </div>
                      <ArrowRight
                        size={14}
                        className="text-gray-300 dark:text-gray-600 group-hover:text-gray-500 group-active:text-gray-500 transition-colors shrink-0 ml-2"
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Setup checklist */}
      {userRole === 'admin' && completed < setupSteps.length && (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-sm text-gray-800 dark:text-white">
              Setup checklist
            </h2>
            <span className="text-xs text-gray-400">
              {completed}/{setupSteps.length}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 mb-4 overflow-hidden">
            <div
              className="h-full bg-secondary rounded-full transition-all"
              style={{
                width: `${(completed / setupSteps.length) * 100}%`,
              }}
            />
          </div>
          <ul className="space-y-1.5">
            {setupSteps.map((step) => (
              <li key={step.label}>
                <button
                  onClick={() => onNavigate(step.tab as TabId)}
                  className="w-full flex items-center gap-3 text-sm p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50"
                >
                  <span
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                      step.done
                        ? 'bg-emerald-500 text-white'
                        : 'border-2 border-gray-300 dark:border-gray-600'
                    }`}
                  >
                    {step.done ? '✓' : ''}
                  </span>
                  <span
                    className={
                      step.done
                        ? 'text-gray-400 line-through'
                        : 'text-gray-700 dark:text-gray-200'
                    }
                  >
                    {step.label}
                  </span>
                  {!step.done && (
                    <ArrowRight size={14} className="ml-auto text-gray-300" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
